import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocalizeMessage, Table, Spinner, Select } from 'libs.nucleus';
import { AuthContext, useApiClient, ApiClientService } from 'libs.react_internal';
import { useDataTemplatesDashboardColumns } from './data_templates_dashboard_columns.hook';
const PAGE_SIZE = 25;
export const DataTemplatesDashboardContent = () => {
    const translate = useLocalizeMessage();
    const { entityId, user } = useContext(AuthContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const [isArchived, setIsArchived] = useState({ label: translate('No'), value: 'false' });
    const [isGlobal, setIsGlobal] = useState({ label: translate('-'), value: '' });
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
    const cursorsRef = useRef(new Map([[0, undefined]]));
    const statusFilter = useMemo(() => {
        if (isArchived.value === 'true') {
            return ['ARCHIVED'];
        }
        return ['DRAFT', 'PUBLISHED'];
    }, [isArchived]);
    const globalFilter = useMemo(() => isGlobal.value === 'true', [isGlobal]);
    const retrieveTransferTemplates = async () => {
        const cursor = cursorsRef.current.get(pagination.pageIndex);
        const { data } = await webClient.post(`/api/data_transfer/templates/list`, {
            pagination: {
                paginated: true,
                limit: PAGE_SIZE,
                ...(cursor != null && { maxCreatedAt: cursor }),
            },
            filter: {
                status: statusFilter,
                isGlobal: globalFilter,
            },
        });
        if (data.hasMore && data.nextCursor != null) {
            cursorsRef.current.set(pagination.pageIndex + 1, data.nextCursor);
        }
        return data;
    };
    const { data: paginatedResult, isLoading } = useQuery({
        queryKey: ['entities', entityId, 'transfer_templates', pagination.pageIndex, statusFilter, globalFilter],
        queryFn: retrieveTransferTemplates,
        enabled: !!user && !!entityId,
    });
    const { transferTemplateColumns } = useDataTemplatesDashboardColumns();
    const templates = paginatedResult?.data ?? [];
    const pageCount = useMemo(() => {
        if (!paginatedResult) {
            return 0;
        }
        return paginatedResult.hasMore ? pagination.pageIndex + 2 : pagination.pageIndex + 1;
    }, [paginatedResult, pagination.pageIndex]);
    const handlePaginationChange = useCallback((updater) => {
        const next = typeof updater === 'function' ? updater(pagination) : updater;
        if (next.pageIndex < pagination.pageIndex) {
            setPagination(next);
        }
        else if (cursorsRef.current.has(next.pageIndex)) {
            setPagination(next);
        }
    }, [pagination]);
    const handleFilterChange = useCallback((setter) => (value) => {
        cursorsRef.current = new Map([[0, undefined]]);
        setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
        setter(value);
    }, []);
    if (isLoading && !paginatedResult) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    if (!isLoading && templates.length === 0 && pagination.pageIndex === 0) {
        return (_jsx("div", { className: 'flex flex-col items-center justify-center border border-border-primary rounded-lg p-24', children: _jsx("p", { className: 'text-text-secondary', children: translate('Create data transfer template usable across studies.') }) }));
    }
    const renderCustomHeaderContent = () => {
        return (_jsxs("div", { className: 'flex gap-4', children: [_jsx(Select, { label: translate('Is archived'), options: [
                        { label: translate('Yes'), value: 'true' },
                        { label: translate('No'), value: 'false' },
                    ], value: isArchived, onChange: handleFilterChange(setIsArchived), width: 'sm' }), _jsx(Select, { label: translate('Is global'), options: [
                        { label: translate('Yes'), value: 'true' },
                        { label: translate('No'), value: 'false' },
                    ], value: isGlobal, onChange: handleFilterChange(setIsGlobal), width: 'sm' })] }));
    };
    return (_jsx("div", { "data-testid": 'data-templates-table', children: _jsx(Table, { columns: transferTemplateColumns, data: templates, hasPagination: true, initialPageSize: PAGE_SIZE, isLoading: isLoading, labelNoResults: translate('No transfer templates'), paginationNavigationScreenReaderLabel: translate('Transfer templates table navigation'), manualPagination: true, pageCount: pageCount, manualPaginationState: pagination, onPaginationChange: handlePaginationChange, customHeaderContent: renderCustomHeaderContent() }) }));
};
